
import { useEffect } from 'react';

interface SeoProps {
    title?: string;
    description?: string;
    imageUrl?: string;
    keywords?: string;
    type?: 'website' | 'product';
    canonicalUrl?: string;
    jsonLd?: Record<string, any>;
}

const setMetaTag = (attr: 'name' | 'property', value: string, content: string) => {
    // Don't create or update the tag if content is empty
    if (!content) {
        return;
    }
    
    // Check if the tag already exists in the head
    let element = document.head.querySelector(`meta[${attr}='${value}']`) as HTMLMetaElement;
    
    // If it doesn't exist, create it and append it to the head
    if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attr, value);
        document.head.appendChild(element);
    }
    
    // Set the content of the tag
    element.setAttribute('content', content);
};


const defaultTitle = "Impresión Veloz - Tu Imprenta con Entrega Express";
const defaultDescription = "Tu imprenta digital con entrega express. Ofrecemos una configuración de productos sencilla y una experiencia de compra con seguimiento en tiempo real, enfocada en la velocidad y calidad.";
const defaultImage = "https://i.imgur.com/2s4x7x8.png"; // Default image for the site

export const useSeo = ({ title, description, imageUrl, keywords, type = 'website', canonicalUrl, jsonLd }: SeoProps) => {
    useEffect(() => {
        // Use provided values or fall back to defaults
        const currentTitle = title || defaultTitle;
        const currentDescription = description || defaultDescription;
        const currentImageUrl = imageUrl || defaultImage;
        const currentUrl = window.location.href;

        // Set document title
        document.title = currentTitle;

        // Standard SEO
        setMetaTag('name', 'description', currentDescription);
        if (keywords) {
           setMetaTag('name', 'keywords', keywords);
        }

        // Canonical URL
        const canonical = canonicalUrl || currentUrl.split('?')[0].split('#')[0];
        let linkCanonical = document.head.querySelector("link[rel='canonical']") as HTMLLinkElement | null;
        if (!linkCanonical) {
            linkCanonical = document.createElement('link');
            linkCanonical.rel = 'canonical';
            document.head.appendChild(linkCanonical);
        }
        linkCanonical.href = canonical;

        // Open Graph
        setMetaTag('property', 'og:locale', 'es_MX');
        setMetaTag('property', 'og:title', currentTitle);
        setMetaTag('property', 'og:description', currentDescription);
        setMetaTag('property', 'og:image', currentImageUrl);
        setMetaTag('property', 'og:logo', currentImageUrl);
        setMetaTag('property', 'og:url', canonical);
        setMetaTag('property', 'og:type', type);

        // Twitter Card
        setMetaTag('name', 'twitter:title', currentTitle);
        setMetaTag('name', 'twitter:description', currentDescription);
        setMetaTag('name', 'twitter:image', currentImageUrl);

    }, [title, description, imageUrl, keywords, type, canonicalUrl]);

    // JSON-LD structured data
    useEffect(() => {
        if (!jsonLd) return;
        const id = 'seo-json-ld';
        let script = document.getElementById(id) as HTMLScriptElement | null;
        if (!script) {
            script = document.createElement('script');
            script.id = id;
            script.type = 'application/ld+json';
            document.head.appendChild(script);
        }
        script.textContent = JSON.stringify(jsonLd);
        return () => {
            script?.remove();
        };
    }, [jsonLd]);
};
